import React, { useState, useEffect, useRef } from 'react';
import { trainingAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { BookOpen, Plus, Trash2, Upload, FileText, Video, ChevronDown, ChevronRight, GraduationCap } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const CATEGORIES = [
  { value: 'basics', label: 'Basics', color: 'bg-blue-50 text-blue-700' },
  { value: 'vouchers', label: 'Vouchers', color: 'bg-emerald-50 text-emerald-700' },
  { value: 'ledger', label: 'Ledger', color: 'bg-purple-50 text-purple-700' },
  { value: 'automation', label: 'Automation', color: 'bg-orange-50 text-orange-700' },
  { value: 'compliance', label: 'Compliance', color: 'bg-rose-50 text-rose-700' },
  { value: 'general', label: 'General', color: 'bg-slate-100 text-slate-700' },
];

export default function TrainingCenter() {
  const { user } = useAuth();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('');
  const [expanded, setExpanded] = useState({});
  const [createDialog, setCreateDialog] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', content_type: 'text', category: 'general', order: 0 });
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeCategory) params.category = activeCategory;
      const res = await trainingAPI.list(params);
      setModules(res.data);
    } catch { toast.error('Failed to load training modules'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [activeCategory]);

  const handleCreate = async () => {
    if (!form.title) { toast.error('Title required'); return; }
    try {
      await trainingAPI.create(form);
      toast.success('Module created');
      setCreateDialog(false);
      setForm({ title: '', content: '', content_type: 'text', category: 'general', order: 0 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleUpload = async (moduleId) => {
    fileRef.current?.click();
    fileRef.current.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        await trainingAPI.uploadFile(moduleId, file);
        toast.success('File uploaded');
        load();
      } catch { toast.error('Upload failed'); }
    };
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this module?')) return;
    try {
      await trainingAPI.delete(id);
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed'); }
  };

  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));
  const isAdmin = user?.role === 'admin';
  const catConfig = (cat) => CATEGORIES.find(c => c.value === cat) || CATEGORIES[5];

  return (
    <div data-testid="training-center-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-orange-100 flex items-center justify-center">
            <GraduationCap className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">Training Center</h1>
            <p className="text-sm text-slate-500">Learn how to use Transsolution accounting features</p>
          </div>
        </div>
        {isAdmin && (
          <Button onClick={() => setCreateDialog(true)} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="tc-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Module
          </Button>
        )}
      </div>

      {/* Category Filters */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setActiveCategory('')}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${!activeCategory ? 'bg-orange-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          data-testid="tc-all">All</button>
        {CATEGORIES.map(c => (
          <button key={c.value} onClick={() => setActiveCategory(c.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${activeCategory === c.value ? 'bg-orange-600 text-white' : c.color}`}
            data-testid={`tc-cat-${c.value}`}>{c.label}</button>
        ))}
      </div>

      {/* Modules */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-3">
          {modules.map(m => {
            const cat = catConfig(m.category);
            return (
              <div key={m.module_id} className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={`tc-module-${m.module_id}`}>
                <button onClick={() => toggle(m.module_id)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3">
                    {m.content_type === 'video' ? <Video className="w-5 h-5 text-purple-500" /> : m.content_type === 'pdf' ? <FileText className="w-5 h-5 text-rose-500" /> : <BookOpen className="w-5 h-5 text-blue-500" />}
                    <div className="text-left">
                      <p className="font-semibold text-sm text-slate-800">{m.title}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${cat.color}`}>{cat.label}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isAdmin && (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); handleUpload(m.module_id); }} className="p-1.5 hover:bg-slate-200 rounded" data-testid={`tc-upload-${m.module_id}`}>
                          <Upload className="w-3.5 h-3.5 text-slate-400" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(m.module_id); }} className="p-1.5 hover:bg-rose-50 rounded" data-testid={`tc-delete-${m.module_id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                        </button>
                      </>
                    )}
                    {expanded[m.module_id] ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  </div>
                </button>
                {expanded[m.module_id] && (
                  <div className="px-5 pb-5 pt-1 border-t border-slate-100">
                    {m.content_type === 'text' && m.content && (
                      <div className="prose prose-sm max-w-none text-slate-700 leading-relaxed">
                        {m.content.split('\n').map((line, i) => {
                          const key = `${m.module_id}-line-${i}`;
                          if (line.startsWith('## ')) return <h3 key={key} className="font-bold text-base text-slate-800 mt-3 mb-1">{line.replace('## ', '')}</h3>;
                          if (line.startsWith('### ')) return <h4 key={key} className="font-semibold text-sm text-slate-700 mt-2 mb-1">{line.replace('### ', '')}</h4>;
                          if (line.startsWith('- ')) return <li key={key} className="ml-4 text-sm">{line.replace('- ', '')}</li>;
                          if (line.startsWith('**') && line.endsWith('**')) return <p key={key} className="font-bold text-sm mt-1">{line.replace(/\*\*/g, '')}</p>;
                          if (line.trim() === '') return <br key={key} />;
                          return <p key={key} className="text-sm">{line}</p>;
                        })}
                      </div>
                    )}
                    {m.content_type === 'video' && m.video_url && (
                      <video controls className="w-full rounded-md mt-2" style={{ maxHeight: 400 }}>
                        <source src={m.video_url} />
                      </video>
                    )}
                    {m.content_type === 'pdf' && m.video_url && (
                      <a href={m.video_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-orange-600 hover:underline mt-2">
                        <FileText className="w-4 h-4" /> View PDF
                      </a>
                    )}
                    {(!m.content && !m.video_url) && (
                      <p className="text-sm text-slate-400 italic">No content yet. {isAdmin ? 'Upload a file or add text content.' : ''}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {modules.length === 0 && <p className="text-center text-slate-400 py-12">No training modules available</p>}
        </div>
      )}

      <input type="file" ref={fileRef} className="hidden" accept=".mp4,.webm,.pdf" />

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add Training Module</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="form-label">Title</label>
              <input type="text" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} className="form-input" data-testid="tc-form-title" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Category</label>
                <select value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} className="form-input" data-testid="tc-form-category">
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Content Type</label>
                <select value={form.content_type} onChange={(e) => setForm(f => ({ ...f, content_type: e.target.value }))} className="form-input" data-testid="tc-form-type">
                  <option value="text">Text Guide</option>
                  <option value="video">Video</option>
                  <option value="pdf">PDF</option>
                </select>
              </div>
            </div>
            {form.content_type === 'text' && (
              <div>
                <label className="form-label">Content (Markdown supported)</label>
                <textarea value={form.content} onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))}
                  className="form-input min-h-[120px] resize-y font-mono text-sm" data-testid="tc-form-content" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="tc-form-save">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
